import { useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { Button } from '@heroui/react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { FaPlus, FaArrowRight, FaRotate, FaFilm, FaEye, FaCheck, FaLink, FaFilter, FaGlobe, FaBolt } from 'react-icons/fa6';
import { autoDubWorkflowsState } from '../../store.jsx';
import repositories from '../../repositories.jsx';
import { useAutoDubChannels, useAutoDubWorkflows } from '../../hooks/useAutoDubData.jsx';
import Header from '../../components/Header.jsx';
import PageLoader from '../../components/PageLoader.jsx';
import WorkflowCard from '../../components/autoDub/WorkflowCard.jsx';
import ReconnectBanner from '../../components/autoDub/ReconnectBanner.jsx';
import YouTubeBadge from '../../components/autoDub/primitives/YouTubeBadge.jsx';

/* ---- Gradient command banner — overview + primary action ---- */
function CommandBanner({ count, active, onCreate }) {
	return (
		<motion.div
			initial={{ opacity: 0, y: -12 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ type: 'spring', stiffness: 220, damping: 26 }}
			className="relative overflow-hidden rounded-3xl px-6 py-5 md:px-7 md:py-6 mb-6 text-white bg-gradient-to-br from-primary via-secondary to-fuchsia-600"
		>
			<motion.span aria-hidden className="pointer-events-none absolute -top-20 -right-10 h-64 w-64 rounded-full bg-white/20 blur-3xl"
				animate={{ x: [0, -20, 0], y: [0, 16, 0] }} transition={{ duration: 13, repeat: Infinity, ease: 'easeInOut' }} />
			<motion.span aria-hidden className="pointer-events-none absolute -bottom-24 left-1/3 h-64 w-64 rounded-full bg-fuchsia-300/25 blur-3xl"
				animate={{ x: [0, 24, 0], y: [0, -14, 0] }} transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut' }} />
			<span aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.12] bg-[linear-gradient(to_right,rgba(255,255,255,.6)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,.6)_1px,transparent_1px)] [background-size:26px_26px]" />

			<div className="relative flex flex-col md:flex-row md:items-center justify-between gap-4">
				<div>
					<div className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.14em] uppercase text-white/80 mb-1.5">
						<FaBolt size={13} /> YouTube automation
					</div>
					<h1 className="m-0 text-[24px] md:text-[27px] font-bold tracking-tight leading-tight">
						<span className="tabular-nums">{count}</span> {count === 1 ? 'automation' : 'automations'}
						{count > 0 && <span className="text-white/70 font-semibold text-[18px] md:text-[20px]"> · {active} active</span>}
					</h1>
					<p className="mt-1 text-[13.5px] text-white/80 max-w-lg leading-snug">
						Automatically dub new uploads and publish them to your destination channels.
					</p>
				</div>

				<Button
					size="lg"
					radius="full"
					className="bg-white text-primary font-semibold shadow-lg shadow-black/10 hover:scale-[1.02] transition-transform self-start md:self-auto"
					startContent={<FaPlus size={15} />}
					onPress={onCreate}
				>
					Create automation
				</Button>
			</div>
		</motion.div>
	);
}

/* ---- Stat tile ---- */
function StatCard({ icon, color, value, label, index = 0 }) {
	return (
		<motion.div
			initial={{ opacity: 0, y: 12 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ delay: 0.05 * index, type: 'spring', stiffness: 260, damping: 24 }}
			whileHover={{ y: -4 }}
			className="relative overflow-hidden bg-white rounded-3xl border border-slate-200/80 shadow-[0_10px_40px_-28px_rgba(15,23,42,0.35)] hover:shadow-[0_18px_44px_-24px_rgba(15,23,42,0.28)] transition-shadow px-5 py-[18px] flex items-center gap-3.5 flex-1 min-w-[180px]"
		>
			<span aria-hidden className="absolute -right-7 -top-7 h-20 w-20 rounded-full blur-2xl" style={{ background: color + '26' }} />
			<div className="relative w-11 h-11 rounded-[13px] grid place-items-center flex-none text-white shadow-sm" style={{ background: color, boxShadow: `0 8px 20px -8px ${color}` }}>{icon}</div>
			<div className="relative">
				<div className="text-2xl font-bold text-slate-800 leading-none tabular-nums">{value}</div>
				<div className="text-[13px] text-slate-500 mt-1">{label}</div>
			</div>
		</motion.div>
	);
}

/* ---- Empty state ---- */
function EmptyState({ onCreate }) {
	const steps = [
		[<FaLink key="i" size={17} />, 'Connect channel'],
		[<FaFilter key="i" size={17} />, 'Set filters'],
		[<FaGlobe key="i" size={17} />, 'Pick languages'],
		[<FaBolt key="i" size={17} />, 'Auto-publish'],
	];
	return (
		<motion.div
			initial={{ opacity: 0, y: 16 }}
			animate={{ opacity: 1, y: 0 }}
			className="relative overflow-hidden bg-white rounded-3xl border border-slate-200 px-10 py-16 text-center grid place-items-center"
		>
			<span aria-hidden className="pointer-events-none absolute -top-16 left-1/4 h-48 w-48 rounded-full bg-primary/10 blur-3xl" />
			<span aria-hidden className="pointer-events-none absolute -bottom-16 right-1/4 h-48 w-48 rounded-full bg-youtube/10 blur-3xl" />
			<motion.div
				className="relative w-[78px] h-[78px] rounded-[22px] bg-youtube_soft grid place-items-center mb-5"
				animate={{ y: [0, -6, 0] }} transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}>
				<YouTubeBadge size={46} />
			</motion.div>
			<h3 className="relative m-0 mb-2 text-[22px] font-semibold text-slate-800">Set your channel on autopilot</h3>
			<p className="relative text-sm text-slate-500 max-w-[440px]">Link a source YouTube channel, choose your languages and destination channels, and every matching video gets dubbed and published automatically.</p>
			<div className="relative flex gap-7 my-7 flex-wrap justify-center">
				{steps.map(([ic, t], i) => (
					<motion.div key={t} className="flex items-center gap-2.5"
						initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.08 }}>
						<div className="w-[38px] h-[38px] rounded-xl bg-primary/10 text-primary grid place-items-center">{ic}</div>
						<span className="text-[13.5px] font-semibold text-slate-600">{t}</span>
						{i < 3 && <FaArrowRight size={14} className="text-slate-300" />}
					</motion.div>
				))}
			</div>
			<Button color="primary" size="lg" radius="full" className="relative" startContent={<FaPlus size={16} />} onPress={onCreate}>Create automation</Button>
		</motion.div>
	);
}

export default function AutoDubLanding() {
	const navigate = useNavigate();
	const [workflows, setWorkflows] = useRecoilState(autoDubWorkflowsState);
	const { ready } = useAutoDubWorkflows();
	const { allChannels } = useAutoDubChannels();

	const reconnectCount = allChannels.filter(c => c.needs_reconnect || !c.connected).length;

	const onCreate = () => navigate('/auto-dub/create');
	const onToggle = (wf) => {
		const prev = wf.status;
		const next = prev === 'active' ? 'paused' : 'active';
		setWorkflows(ws => ws.map(w => w.id === wf.id ? { ...w, status: next } : w));
		repositories.autoDub.workflows.update(wf.id, { status: next }).catch(() => {
			setWorkflows(ws => ws.map(w => w.id === wf.id ? { ...w, status: prev } : w));
			toast.error('Could not update the workflow. Please try again.');
		});
	};

	const totals = workflows.reduce((a, w) => ({
		active: a.active + (w.status === 'active' ? 1 : 0),
		queue: a.queue + w.stats.queue + w.stats.dubbing,
		review: a.review + w.stats.review,
		published: a.published + w.stats.published,
	}), { active: 0, queue: 0, review: 0, published: 0 });

	return (
		<div className="relative">
			<Header currentPage="auto-dub" />
			<div className="py-6">
				{!ready && workflows.length === 0 ? (
					<PageLoader />
				) : workflows.length === 0 ? (
					<>
						<CommandBanner count={0} active={0} onCreate={onCreate} />
						{reconnectCount > 0 && (
							<ReconnectBanner count={reconnectCount} onGoToChannels={() => navigate('/channels')} />
						)}
						<EmptyState onCreate={onCreate} />
					</>
				) : (
					<>
						<CommandBanner count={workflows.length} active={totals.active} onCreate={onCreate} />
						{reconnectCount > 0 && (
							<ReconnectBanner count={reconnectCount} onGoToChannels={() => navigate('/channels')} />
						)}
						<div className="flex gap-4 flex-wrap mb-5">
							<StatCard index={0} icon={<FaRotate size={20} />} color="#FF0033" value={totals.active} label="Active workflows" />
							<StatCard index={1} icon={<FaFilm size={20} />} color="#5A0AE0" value={totals.queue} label="Videos in queue" />
							<StatCard index={2} icon={<FaEye size={20} />} color="#f6a609" value={totals.review} label="Awaiting review" />
							<StatCard index={3} icon={<FaCheck size={20} />} color="#16c098" value={totals.published.toLocaleString()} label="Published all-time" />
						</div>
						<div className="flex flex-col gap-4">
							{workflows.map((wf, i) => <WorkflowCard key={wf.id} wf={wf} onToggle={onToggle} index={i} />)}
						</div>
					</>
				)}
			</div>
		</div>
	);
}
